// slices/auth/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetPosthog } from '../../utils/posthogClient';
import { ADMIN_STEP_UP_STORAGE_KEY } from '../../services/adminStepUpStorage';
import { SESSION_SYNC_STORAGE_KEY } from '../../services/sessionSync';
import type { RootState } from '../../store/store';

// Define status types
type AuthStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/**
 * Admin impersonation is server-side session state (ORA-987): the session
 * cookie, shared by every tab, now belongs to the student. This key is the
 * matching cross-tab record of who started it; the slice mirrors it so React
 * can subscribe. Malformed JSON reads as "not impersonating".
 */
export const IMPERSONATION_STORAGE_KEY = 'ora-impersonation';

export interface ImpersonationRecord {
  /** The admin's user id. */
  impersonatedBy: string;
}

export const readStoredImpersonation = (): ImpersonationRecord | null => {
  try {
    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as { impersonatedBy?: unknown };
    return typeof parsed?.impersonatedBy === 'string' && parsed.impersonatedBy
      ? { impersonatedBy: parsed.impersonatedBy }
      : null;
  } catch {
    return null;
  }
};

interface AuthState {
  /** The signed-in user's id, from the session (never a token). */
  userId: string | null;
  /** The impersonating admin's id while an admin acts as this user. */
  impersonatedBy: string | null;
  isAuthenticated: boolean;
  status: AuthStatus;
  error: string | null;
}

// The session itself is an httpOnly cookie the frontend cannot read; whether
// it is still valid is learned from the API (redux-persist restores the last
// known state, and any 401 signs the tab out).
const getInitialAuthState = (): AuthState => ({
  userId: null,
  impersonatedBy: readStoredImpersonation()?.impersonatedBy ?? null,
  isAuthenticated: false,
  status: 'idle',
  error: null,
});

const initialState: AuthState = getInitialAuthState();

/** Everything a signed-out browser must not keep. */
const clearAuthStorage = () => {
  // Legacy JWT keys (ORA-987): normally already traded and removed on boot.
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('originalAdminSession');
  localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  localStorage.removeItem(ADMIN_STEP_UP_STORAGE_KEY); // ORA-864
  // The last cross-tab session event (its account id), when the browser has
  // no BroadcastChannel.
  localStorage.removeItem(SESSION_SYNC_STORAGE_KEY);
  localStorage.removeItem('persist:root');
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Action dispatched when a sign-in starts
    authLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    // Action dispatched once the backend has set a session cookie
    authSuccess: (
      state,
      action: PayloadAction<{
        userId: string;
        isAuthenticated?: boolean;
      }>
    ) => {
      const { userId, isAuthenticated } = action.payload;

      state.userId = userId;
      state.isAuthenticated = isAuthenticated ?? true;
      state.status = 'succeeded';
      state.error = null;
    },
    // The password was accepted; the session starts once the second factor
    // is verified on the 2FA page.
    authSecondFactorRequired: (state) => {
      state.status = 'idle';
      state.error = null;
    },
    impersonationStarted: (
      state,
      action: PayloadAction<ImpersonationRecord>
    ) => {
      localStorage.setItem(
        IMPERSONATION_STORAGE_KEY,
        JSON.stringify(action.payload)
      );

      state.impersonatedBy = action.payload.impersonatedBy;
    },
    impersonationStopped: (state) => {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      state.impersonatedBy = null;
    },
    // The session now belongs to another account than this tab's state
    // (another tab signed in or started/stopped impersonating, or the boot
    // check found a different account). The root reducer resets every other
    // slice on it, like `logout`, without touching storage.
    sessionIdentityChanged: (
      state,
      action: PayloadAction<{ userId: string; impersonatedBy: string | null }>
    ) => {
      state.userId = action.payload.userId;
      state.impersonatedBy = action.payload.impersonatedBy;
      state.isAuthenticated = true;
      state.status = 'succeeded';
      state.error = null;
    },
    // Action dispatched on a failed sign-in
    authFailed: (state, action: PayloadAction<string | null>) => {
      clearAuthStorage();
      state.userId = null;
      state.impersonatedBy = null;
      state.isAuthenticated = false;
      state.status = 'failed';
      state.error = action.payload ?? 'Authentication failed';
    },
    // Action for explicit logout (and for a session the backend no longer
    // accepts). The root reducer also wipes every other slice on it.
    logout: (state) => {
      clearAuthStorage();
      resetPosthog(); // Resets distinct_id for the user
      state.userId = null;
      state.impersonatedBy = null;
      state.isAuthenticated = false;
      state.status = 'idle'; // Reset status
      state.error = null;
    },
  },
});

export const {
  authLoading,
  authSuccess,
  authSecondFactorRequired,
  authFailed,
  logout,
  impersonationStarted,
  impersonationStopped,
  sessionIdentityChanged,
} = authSlice.actions;

// The NavBar and Onboarding derive their "Stop Impersonating" control from
// this, so it tracks impersonation start/stop without a remount.
export const selectIsImpersonating = (state: RootState): boolean =>
  Boolean(state.auth.impersonatedBy);

export default authSlice.reducer;
